import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentPageTitle } from "@/contexts/PageTitleContext";
import { useNotifications } from "@/hooks/useNotifications";
import { useOnboardingGate } from "@/components/routing/OnboardingGate";
import { ProfileSheet } from "./ProfileSheet";
import { OnboardingHelpButton } from "@/components/onboarding/OnboardingHelpButton";

/**
 * Shared band gradient used by every header + the sidebar, across all roles,
 * so admin/manager and worker apps feel like a single product. Defined once as
 * the --band token (index.css).
 */
export const HEADER_GRADIENT = "var(--band)";

/**
 * El hueco de arriba de la barra, el mismo para TODAS las variantes.
 *
 * Antes cada rama se lo escribía a mano y habían divergido: las variantes alta y
 * de subpágina ponían `max(env(safe-area-inset-top), 44px) + 6px` y la de por
 * defecto `var(--safe-area-top, env(...)) + 6px`, sin suelo. En un navegador sin
 * muesca eso son 50 px contra 6 px, y por eso al navegar la barra pegaba el
 * salto. El cálculo vive ahora en un token de `index.css` para que no se pueda
 * volver a partir en tres copiando y pegando.
 */
export const TOPBAR_TOP_GAP = "var(--topbar-top-gap)";

/**
 * La carcasa de la banda: lo que hace que las cuatro variantes MIDAN LO MISMO.
 *
 * Todo lo que afecta a la altura o a la silueta está aquí y solo aquí — el
 * `pb-4`, el radio de abajo y el `px-4`— para que la única diferencia entre una
 * pantalla principal y una subpágina sea lo que va DENTRO de la fila. Se quedó
 * fuera a propósito la sombra que llevaba la variante de por defecto: como las
 * otras no la tienen, aparecía y desaparecía al navegar, que es justo el tipo de
 * parpadeo que veníamos a quitar.
 *
 * `PeritoTopBar` importa esta misma constante; si aquí cambia el alto, cambia
 * también allí, que es lo que se quiere.
 */
export const TOPBAR_SHELL_CLASS =
  "md:hidden shrink-0 z-40 relative overflow-hidden text-white rounded-b-[24px] px-4 pb-4";

/**
 * La fila de la cabecera. Mide 40 px porque el elemento más alto es un botón
 * redondo de 40 (el avatar o la flecha de atrás): con eso, el `pb-4` y el hueco
 * de arriba, la altura sale sola y no hace falta ningún `min-h`.
 */
export const TOPBAR_ROW_CLASS = "relative flex items-center gap-3";

/**
 * El título, SIEMPRE a la izquierda y con el mismo tamaño.
 *
 * Las subpáginas lo centraban en 17 px, así que al entrar en una desde su
 * pantalla principal el texto se encogía y se iba al medio, aunque la flecha de
 * atrás ocupe exactamente el sitio que dejaba el avatar. Alineado a la izquierda
 * cae en la misma columna en las dos, y la navegación deja de moverlo.
 * El `min-w-0` no es decorativo: sin él un título largo estira la fila en vez de
 * recortarse con puntos suspensivos.
 */
export const TOPBAR_TITLE_CLASS =
  "flex-1 min-w-0 text-[19px] font-bold tracking-tight truncate";

/**
 * El botón redondo de 40 px: avatar y flecha de atrás miden igual a propósito.
 *
 * Va EXPORTADO aunque solo lo use el perito porque es la clase que de verdad
 * decide el alto de la fila —no hay `min-h` en ningún sitio: la fila mide lo que
 * mida su hijo más alto, y su hijo más alto es este botón—. Compartir la
 * carcasa y dejarse el botón duplicado habría dejado el arreglo a medias: bastaba
 * cambiar este 40 por un 44 aquí para que las dos cabeceras volvieran a medir
 * distinto, que es exactamente como se separaron la vez anterior.
 */
export const TOPBAR_BACK_BUTTON_CLASS =
  "inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white active:scale-95 transition-transform flex-shrink-0";
const TOPBAR_AVATAR_CLASS =
  "relative w-10 h-10 rounded-full bg-white/[0.18] backdrop-blur-md flex items-center justify-center text-white font-bold text-base ring-1 ring-white/40 active:scale-95 transition-transform flex-shrink-0";

export function MobileTopBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const {
    title,
    actions,
    leftActions,
    mobileHeaderMode,
    onMobileBack,
    hideMobileTopBar,
    mobileActions,
    mobileTitle,
  } = useCurrentPageTitle();
  const { unreadCount } = useNotifications();
  const { active: onboardingActive } = useOnboardingGate();
  const [profileOpen, setProfileOpen] = useState(false);

  if (hideMobileTopBar) return null;

  const fullName = user?.user_metadata?.full_name || user?.email || "U";
  const initial = fullName.charAt(0).toUpperCase();
  const hasUnread = (unreadCount ?? 0) > 0;

  const showOnboardingBack =
    onboardingActive && location.pathname !== "/getting-started";

  // ---------- TALL variant (worker-style, used on main mobile pages) ----------
  if (mobileHeaderMode === "tall") {
    const effectiveActions = mobileActions ?? actions;
    const effectiveTitle = mobileTitle ?? title;
    return (
      <>
        <header
          data-app-topbar
          className={TOPBAR_SHELL_CLASS}
          style={{
            paddingTop: TOPBAR_TOP_GAP,
            background: HEADER_GRADIENT,
          }}
        >
          {/* Los dos globos son decoración de las pantallas principales: van en
              `absolute`, así que no suman altura ni desplazan la fila. */}
          <div
            aria-hidden
            className="absolute -right-12 -top-8 w-40 h-40 rounded-full bg-white/10"
          />
          <div
            aria-hidden
            className="absolute -right-20 top-10 w-36 h-36 rounded-full bg-white/5"
          />

          <div className={TOPBAR_ROW_CLASS}>
            <button
              onClick={() => setProfileOpen(true)}
              className={TOPBAR_AVATAR_CLASS}
              aria-label={t("nav.profile", "Profile")}
            >
              {initial}
              {hasUnread && (
                <span className="absolute top-0 right-0 w-2.5 h-2.5 rounded-full bg-rose-500 ring-2 ring-white/80" />
              )}
            </button>

            <h1 className={TOPBAR_TITLE_CLASS}>{effectiveTitle}</h1>

            {(effectiveActions || onboardingActive) && (
              <div className="flex items-center gap-2 flex-shrink-0">
                {effectiveActions}
                {onboardingActive && <OnboardingHelpButton />}
              </div>
            )}
          </div>

          {/* Única cosa que puede añadir altura, y solo aquí: una segunda fila.
              Hoy no la usa nadie, pero si vuelve, que vuelva en un sitio. */}
          {leftActions && !onboardingActive && (
            <div className="relative flex items-center gap-2 mt-3">
              {leftActions}
            </div>
          )}
        </header>

        <ProfileSheet open={profileOpen} onOpenChange={setProfileOpen} />
      </>
    );
  }

  // ---------- SUB-TALL variant (back arrow + title, same box as tall) ----------
  if (mobileHeaderMode === "sub-tall") {
    return (
      <header
        data-app-topbar
        className={TOPBAR_SHELL_CLASS}
        style={{
          paddingTop: TOPBAR_TOP_GAP,
          background: HEADER_GRADIENT,
        }}
      >
        <div className={TOPBAR_ROW_CLASS}>
          <button
            type="button"
            onClick={() => (onMobileBack ? onMobileBack() : navigate(-1))}
            className={TOPBAR_BACK_BUTTON_CLASS}
            aria-label={t("common.back")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className={TOPBAR_TITLE_CLASS}>{title}</h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            {mobileActions ?? actions}
            {onboardingActive && <OnboardingHelpButton />}
          </div>
        </div>
      </header>
    );
  }

  // ---------- DEFAULT (slim) and BACK-ONLY variants ----------
  /**
   * Esta es la que se ve cuando el contexto se queda sin modo: las pantallas que
   * no piden ninguno (Ajustes › Cuenta, Facturación, el carrito…) y, sobre todo,
   * el rato en el que la pantalla vieja ya se ha ido y la nueva todavía está
   * descargándose. Por eso lleva la MISMA carcasa y la misma fila que las otras
   * dos: si en ese rato la barra midiera distinto, el encogimiento sería lo único
   * que se movería en pantalla.
   *
   * Lo que la carcasa compartida NO tapa: en ese hueco esta variante enseña el
   * avatar, también cuando se venía de una subpágina que tenía la flecha de
   * atrás. Mide igual, pero el botón de la izquierda hace otra cosa. El porqué de
   * no arreglarlo aquí está en `usePageTitle` (PageTitleContext): estas pantallas
   * sin modo necesitan ese avatar, es su único acceso al perfil.
   */
  const backOnly = mobileHeaderMode === "back-only";
  return (
    <>
      <header
        data-app-topbar
        className={TOPBAR_SHELL_CLASS}
        style={{
          paddingTop: TOPBAR_TOP_GAP,
          background: HEADER_GRADIENT,
        }}
      >
        <div className={TOPBAR_ROW_CLASS}>
          {backOnly ? (
            <button
              onClick={() => onMobileBack?.()}
              className={TOPBAR_BACK_BUTTON_CLASS}
              aria-label={t("common.back")}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          ) : (
            <>
              <button
                onClick={() => setProfileOpen(true)}
                className={TOPBAR_AVATAR_CLASS}
                aria-label={t("nav.profile", "Profile")}
              >
                {initial}
                {hasUnread && (
                  <span className="absolute top-0 right-0 w-2.5 h-2.5 rounded-full bg-rose-500 ring-2 ring-white/80" />
                )}
              </button>
              {/* Era `w-11 h-11`: 44 px en una fila de 40, así que durante el
                  onboarding la barra crecía 4 px respecto a todas las demás. */}
              {showOnboardingBack && (
                <button
                  onClick={() => navigate("/getting-started")}
                  className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 text-white active:scale-95 transition-transform flex-shrink-0"
                  aria-label={t("common.back")}
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
            </>
          )}

          {!backOnly && leftActions && !onboardingActive && (
            <div className="flex items-center gap-2 flex-shrink-0">{leftActions}</div>
          )}

          <h1 className={TOPBAR_TITLE_CLASS}>{title}</h1>

          <div className="flex items-center gap-2 flex-shrink-0">
            {backOnly ? mobileActions : actions}
            {onboardingActive && <OnboardingHelpButton />}
          </div>
        </div>
      </header>

      <ProfileSheet open={profileOpen} onOpenChange={setProfileOpen} />
    </>
  );
}
